import type { PluginContentObject } from '../../lib/flotiq-client/types/api-response';
import type { MarketplacePlugin } from '../../lib/flotiq-client/types/external-api-response';
import type { FlotiqPluginInfo } from '../../lib/flotiq-plugins/types';
import type { UpdatePluginFn } from './ManageModal/ManageModal';

import { use, useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Trans, useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';

import { getEnv } from '../../lib/env';
import {
  batchPatchContentObject,
  deleteContentObject,
  patchContentObject,
  postContentObject,
} from '../../lib/flotiq-client';
import {
  defaultResponseParser,
  fetchMethod,
} from '../../lib/flotiq-client/base-request';
import {
  parseResponseErrors,
  ResponseError,
} from '../../lib/flotiq-client/response-errors';
import FlotiqPlugins, {
  getAllowedPluginUrl,
} from '../../lib/flotiq-plugins/flotiqPluginsRegistry';
import { PluginSettingsChangedEvent } from '../../lib/flotiq-plugins/plugin-events/PluginSettingsChangedEvent';
import { getTestProps, sendEventAndClear } from '../../lib/helpers';

import Heading from '../../components/Heading/Heading';
import LinkButton from '../../components/LinkButton/LinkButton';
import Loader from '../../components/Loader/Loader';
import ProgressBar from '../../components/ProgressBar/ProgressBar';
import TopbarBreadcrumbs from '../../components/Topbar/breadcrumbs/TopbarBreadcrumbs';
import TopbarButton from '../../components/Topbar/buttons/base/TopbarButton';

import {
  useContentTypes,
  useMarketplacePlugins,
  usePluginsSettings,
} from '../../hooks/api';
import useApiErrorsToast from '../../hooks/api/useApiErrorsToast';
import useSelectedSpace from '../../hooks/useSelectedSpace';
import { useSpaceLimits } from '../../hooks/useSpaceLimits';
import useToken from '../../hooks/useToken';

import { useModals } from '../../contexts/ModalContext';
import UserContext from '../../contexts/UserContext';

import CustomUIPluginForm from '../../form/CustomUIPluginForm/CustomUIPluginForm';
import PageLayout, {
  predefinedLayoutClasses,
} from '../../layout/PageLayout/PageLayout';
import ManageModal from './ManageModal/ManageModal';
import PluginItem from './PluginItem/PluginItem';
import useUpdatePluginVersion from './updatePluginVersionHook';

const HOOKS_PARAMS = {
  limit: 1000,
  page: 1,
};

const getPluginType = (pluginId?: string, marketplaceIds?: string[]) => {
  if (!pluginId || !marketplaceIds) return 'custom';
  return marketplaceIds.includes(pluginId) ? 'official' : 'custom';
};

const RESOURCE_NAMES = ['official_plugins_limit', 'custom_plugins_limit'];

const testId = 'plugins';

const Plugins = () => {
  const { t } = useTranslation();
  const jwt = useToken();
  const { space, spaceLimits } = useSelectedSpace();
  const { baseUserEventData, isAdmin } = use(UserContext);

  const modal = useModals();
  const updatePluginVersion = useUpdatePluginVersion();

  const {
    data: marketplace,
    isLoading: marketplaceLoading,
    errors,
  } = useMarketplacePlugins();

  useEffect(() => {
    if (!errors?.length) return;
    toast.error(t('Plugins.FetchError'));
  }, [errors, t]);

  const { data: resources, isLoading: constraintsLoading } = useSpaceLimits(
    spaceLimits,
    RESOURCE_NAMES,
  );

  const allowedHosts = useMemo(
    () => spaceLimits?.allowed_hosts_for_custom_plugins?.split(','),
    [spaceLimits?.allowed_hosts_for_custom_plugins],
  );

  const marketplaceIds = useMemo(
    () => marketplace?.map(({ id }) => id),
    [marketplace],
  );

  const [isSaving, setIsSaving] = useState(false);

  const {
    data: contentTypes,
    isLoading: contentTypesLoading,
    errors: contentTypesErrors,
  } = useContentTypes(HOOKS_PARAMS);

  const {
    data: userPlugins,
    isLoading: userPluginsAreLoading,
    errors: userPluginsErrors,
    reload: reloadUserPlugins,
  } = usePluginsSettings(HOOKS_PARAMS);

  useApiErrorsToast(userPluginsErrors);
  useApiErrorsToast(contentTypesErrors);

  const enabledPluginsIds = useMemo(
    () => userPlugins.map(({ id }) => id),
    [userPlugins],
  );

  const addToPlugins = useCallback(
    async (
      pluginData: FlotiqPluginInfo,
      enabled = true,
    ): Promise<
      [PluginContentObject | null, Record<string, string | string[]> | null]
    > => {
      try {
        const { body, status, ok } = await postContentObject(jwt, space, {
          contentTypeName: '_plugin_settings',
          id: pluginData.id,
          name: pluginData.name,
          url: pluginData.url,
          version: pluginData.version,
          description: pluginData.description,
          manifest: JSON.stringify(pluginData),
          enabled,
        });

        if (!ok) {
          throw parseResponseErrors(body, status);
        }
        return [body as PluginContentObject, null];
      } catch (error) {
        if (!(error instanceof ResponseError)) {
          toast.error(t('Form.CommunicationErrorMessage'));
          return [null, { global: 'Form.CommunicationErrorMessage' }];
        } else {
          toast.error(
            error.message ||
              t('Plugins.UpdatingError', { pluginName: pluginData.name }),
          );
        }
        return [null, error.errors];
      }
    },
    [jwt, space, t],
  );

  const updatePlugin = useCallback<UpdatePluginFn>(
    async (id, pluginName, enabled, settings) => {
      try {
        const { body, status, ok } = await patchContentObject(jwt, space, {
          contentTypeName: '_plugin_settings',
          id,
          name: pluginName,
          enabled,
          ...(settings ? { settings } : {}),
        });

        if (!ok) {
          throw parseResponseErrors(body, status);
        }

        if (settings) {
          FlotiqPlugins.setPluginSettings(id, body.settings);
          FlotiqPlugins.runScoped(
            'flotiq.plugin.settings::changed',
            id,
            new PluginSettingsChangedEvent({ settings: body.settings }),
          );
        }

        return [body as PluginContentObject, {}];
      } catch (error) {
        if (!(error instanceof ResponseError)) {
          toast.error(t('Form.CommunicationErrorMessage'));
          return [null, { global: 'Form.CommunicationErrorMessage' }];
        }

        toast.error(
          error.message || t('Plugins.UpdatingError', { pluginName }),
        );
        return [null, error.errors];
      }
    },
    [jwt, space, t],
  );

  const batchDisablePlugins = useCallback(
    async (enabledPlugin: FlotiqPluginInfo) => {
      try {
        const batchValues = userPlugins.map((plugin) => ({
          id: plugin.id,
          enabled: plugin.id === enabledPlugin.id,
        }));

        const { status, body, ok } = await batchPatchContentObject(
          jwt,
          space,
          '_plugin_settings',
          batchValues,
        );
        if (!ok) {
          throw parseResponseErrors(body, status);
        }

        window.location.reload();
        return true;
      } catch (error) {
        if (!(error instanceof ResponseError)) {
          toast.error(t('Form.CommunicationErrorMessage'));
        } else {
          toast.error(
            t('Plugins.UpdatingError', { pluginName: enabledPlugin.name }),
          );
        }
        return false;
      } finally {
        setIsSaving(false);
      }
    },
    [jwt, space, t, userPlugins],
  );

  const enablePlugin = useCallback(
    async (
      pluginData: FlotiqPluginInfo,
      isNew = true,
      enabled = true,
      pluginsDisabled = false,
    ) => {
      if (pluginsDisabled) {
        return batchDisablePlugins(pluginData);
      }

      setIsSaving(true);
      let result;

      if (isNew) {
        result = await addToPlugins(pluginData);
      } else {
        result = await updatePlugin(pluginData.id, pluginData.name, enabled);
      }

      setIsSaving(false);

      const [newPlugin] = result;
      if (newPlugin) {
        window.location.reload();

        if (!isNew)
          sendEventAndClear(
            {
              event: 'plugin_updated',
              plugin_id: newPlugin.id,
              plugin_type: getPluginType(newPlugin.id, marketplaceIds),
              enabled,
            },
            baseUserEventData,
          );
        else
          sendEventAndClear(
            {
              event: 'plugin_added',
              plugin_id: newPlugin.id,
              plugin_type: 'official',
            },
            baseUserEventData,
          );
      }

      return newPlugin ? newPlugin.enabled : !enabled;
    },
    [
      addToPlugins,
      baseUserEventData,
      batchDisablePlugins,
      marketplaceIds,
      updatePlugin,
    ],
  );

  const openManageModal = useCallback(
    async (userPlugin: PluginContentObject) => {
      await modal({
        title: t('Plugins.ManageTitle', { pluginName: userPlugin.name }),
        content: (
          <ManageModal
            plugin={userPlugin}
            contentTypes={contentTypes}
            updatePlugin={updatePlugin}
            reloadUserPlugins={reloadUserPlugins}
            {...getTestProps(testId, userPlugin.id, 'testId')}
          />
        ),
        size: '2xl',
        dialogAdditionalClasses: 'max-h-[calc(100vh-3rem)]! overflow-visible',
        contentAdditionalClasses: 'min-h-64',
      });
    },
    [modal, t, contentTypes, updatePlugin, reloadUserPlugins],
  );

  const addUIPlugin = useCallback(
    async (url: string) => {
      if (!url) return false;
      setIsSaving(true);

      try {
        const pluginInfoResponse = await fetchMethod(url);
        const { status, body, ok } =
          await defaultResponseParser(pluginInfoResponse);

        if (!ok) {
          throw parseResponseErrors(body, status);
        }

        if (marketplaceIds.includes(body.id)) {
          throw new ResponseError(t('Plugins.ExistingId'));
        }

        try {
          new URL(body.url);
        } catch {
          throw new ResponseError(t('Plugins.WrongURL'));
        }

        if (!getAllowedPluginUrl(body.url)) {
          throw new ResponseError(t('Plugins.HostNotAllowed'));
        }

        const userPlugin = userPlugins?.find(({ id }) => id === body.id);
        if (userPlugin) {
          await updatePluginVersion(
            userPlugin as PluginContentObject,
            body,
            getPluginType(userPlugin.id, marketplaceIds),
          );

          setIsSaving(false);
          return false;
        }

        const [newPlugin] = await addToPlugins(body, false);

        if (!newPlugin) {
          setIsSaving(false);
          return false;
        }

        sendEventAndClear(
          {
            event: 'plugin_added',
            plugin_id: newPlugin.id,
            plugin_type: 'custom',
          },
          baseUserEventData,
        );

        reloadUserPlugins();
        setIsSaving(false);
        return true;
      } catch (error) {
        if (!(error instanceof ResponseError)) {
          toast.error(t('Form.CommunicationErrorMessage'));
        } else {
          toast.error(error.message || t('Plugins.InfoFetchError'));
        }
        setIsSaving(false);
        return false;
      }
    },
    [
      marketplaceIds,
      t,
      userPlugins,
      addToPlugins,
      reloadUserPlugins,
      updatePluginVersion,
      baseUserEventData,
    ],
  );

  const removePlugin = useCallback(
    async (plugin: PluginContentObject) => {
      setIsSaving(true);

      try {
        const { body, status, ok } = await deleteContentObject(jwt, space, {
          contentTypeName: '_plugin_settings',
          id: plugin.id,
        });

        if (!ok) {
          throw parseResponseErrors(body, status);
        }

        sendEventAndClear(
          {
            event: 'plugin_removed',
            plugin_id: plugin.id,
            plugin_type: getPluginType(plugin.id, marketplaceIds),
          },
          baseUserEventData,
        );

        await Promise.all(
          FlotiqPlugins.runScoped(
            'flotiq.plugin::removed',
            plugin.id,
            new PluginSettingsChangedEvent(),
          ),
        );

        window.location.reload();
      } catch (error) {
        if (!(error instanceof ResponseError)) {
          toast.error(t('Form.CommunicationErrorMessage'));
        } else {
          toast.error(
            error.message ||
              t('Plugins.DeletingError', { pluginName: plugin.name }),
          );
        }
      }

      setIsSaving(false);
    },
    [baseUserEventData, jwt, space, marketplaceIds, t],
  );

  const onVersionUpdate = useCallback(
    async (
      plugin: PluginContentObject,
      newPluginVersion: MarketplacePlugin,
    ) => {
      setIsSaving(true);

      await updatePluginVersion(
        plugin,
        newPluginVersion,
        getPluginType(plugin.id, marketplaceIds),
      );

      setIsSaving(false);
    },
    [marketplaceIds, updatePluginVersion],
  );

  const pluginLimits = useMemo(() => {
    return resources.map((resource) => (
      <ProgressBar
        key={resource.key}
        progressBarLabel={resource.label}
        completionPercentValue={resource.percent}
        outOfCompletionTotalValue={resource.limitLabel}
        outOfCompletionValue={resource.value}
        barThickness="thin"
        additionalCompletionValuesContainerClasses="whitespace-nowrap"
        hidePercentValue
      />
    ));
  }, [resources]);

  const officialPluginsLimitReached = resources[0]?.percent >= 100;
  const customPluginsLimitReached = resources[1]?.percent >= 100;

  const pluginsLimitExceeded = useMemo(
    () => resources.filter((resource) => resource.percent > 100).length > 0,
    [resources],
  );

  return (
    <PageLayout
      page="plugins"
      title={t('Global.Plugins')}
      breadcrumbs={<TopbarBreadcrumbs />}
      buttons={
        <TopbarButton
          label={t('Global.Documentation')}
          link={getEnv('VITE_PLUGINS')}
          target="_blank"
          rel="noreferrer"
        />
      }
      testId={testId}
    >
      <div className={predefinedLayoutClasses.withSidebar}>
        <div className={predefinedLayoutClasses.leftColumn}>
          {marketplaceLoading ||
          userPluginsAreLoading ||
          contentTypesLoading ||
          constraintsLoading ? (
            <div className="h-full overflow-hidden flex justify-center items-center">
              <Loader
                type="spinner-grid"
                size="big"
                {...getTestProps(testId, 'loader', 'testId')}
              />
            </div>
          ) : (
            <>
              {(marketplace?.length > 0 || userPlugins?.length > 0) && (
                <>
                  <div className="flex flex-col md:flex-row gap-2 md:items-center lg:mb-4">
                    <Heading
                      level={3}
                      additionalClasses={
                        'pt-0 pb-0 mb-2 md:mb-0 text-xl md:text-3xl leading-none dark:text-slate-200 w-fit'
                      }
                    >
                      Flotiq Plugins
                    </Heading>
                    {isSaving && <Loader type="spinner-grid" size="tiny" />}
                    <div
                      className={twMerge(
                        'py-2 px-4 flex bg-white dark:bg-slate-950 items-start',
                        'items-center justify-between rounded-lg grow space-x-2 xs:space-x-6',
                        'md:max-w-md lg:hidden md:ml-auto my-2 -order-1 md:order-0',
                      )}
                    >
                      {pluginLimits}
                    </div>
                  </div>

                  {pluginsLimitExceeded && (
                    <div className="text-red-600 border border-red-600 bg-white p-5 rounded-lg w-fit mb-4">
                      <Trans
                        i18nKey="Plugins.DisabledDueLimit"
                        components={{
                          1: (
                            <a
                              className="underline hover:text-red-800 focus:text-red-600 active:text-red-600"
                              href={`/space/upgrade/${space}`}
                            />
                          ),
                        }}
                      />
                    </div>
                  )}

                  {allowedHosts?.length > 0 && (
                    <CustomUIPluginForm
                      onSubmit={addUIPlugin}
                      disabled={isSaving}
                      testId={testId}
                    />
                  )}

                  <div className="flex flex-col">
                    {userPlugins?.map((plugin) => {
                      const libraryPlugin = marketplace?.find(
                        (userPlugin) => userPlugin.id === plugin.id,
                      );

                      const newPluginVersion =
                        libraryPlugin &&
                        libraryPlugin?.version !== plugin.version
                          ? libraryPlugin
                          : null;

                      const manifest = plugin.manifest
                        ? JSON.parse(plugin.manifest)
                        : {};

                      return (
                        <PluginItem
                          key={plugin.id}
                          plugin={plugin as PluginContentObject}
                          manifest={manifest}
                          newPluginVersion={newPluginVersion}
                          openManageModal={openManageModal}
                          enablePlugin={enablePlugin}
                          disabled={isSaving}
                          onDelete={removePlugin}
                          onUpdate={onVersionUpdate}
                          limitReached={
                            libraryPlugin
                              ? officialPluginsLimitReached
                              : customPluginsLimitReached
                          }
                          pluginsDisabled={pluginsLimitExceeded}
                          {...getTestProps(testId, plugin.id, 'testId')}
                        />
                      );
                    })}

                    {marketplace
                      ?.filter(({ id }) => !enabledPluginsIds.includes(id))
                      .map((plugin) => (
                        <PluginItem
                          key={plugin.id}
                          plugin={plugin}
                          manifest={plugin}
                          openManageModal={openManageModal}
                          enablePlugin={enablePlugin}
                          onDelete={removePlugin}
                          disabled={isSaving}
                          limitReached={officialPluginsLimitReached}
                          pluginsDisabled={pluginsLimitExceeded}
                          isNew
                          {...getTestProps(testId, plugin.id, 'testId')}
                        />
                      ))}
                  </div>
                  <div className="mt-5 border-t xl:border-0 dark:border-slate-800 h-1 w-full" />
                </>
              )}
            </>
          )}
        </div>
        <div className={predefinedLayoutClasses.rightColumn}>
          <div className="hidden lg:block px-7 py-5 rounded-lg bg-white dark:bg-slate-950 relative h-fit">
            <div
              className={
                'w-full flex flex-wrap gap-x-2 gap-y-1 items-center justify-between ' +
                'mr-2 font-bold text-base dark:text-slate-200'
              }
            >
              {t('Plugins.LimitDetails')}
              {isAdmin && spaceLimits?.price !== -1 && (
                <LinkButton link={`/space/upgrade/${space}`}>
                  {t('Global.UpgradePlan')}
                </LinkButton>
              )}
            </div>
            <div className="flex flex-col 2xl:flex-row gap-6 pt-4 dark:text-slate-200">
              {pluginLimits}
            </div>
          </div>
          <div className="px-7 py-5 rounded-lg bg-white dark:bg-slate-950 relative h-fit">
            <div className="font-bold text-base dark:text-slate-200">
              {t('Plugins.About')}
            </div>
            <div className="pt-4 dark:text-slate-200">
              <Trans
                i18nKey="Plugins.AboutDescription"
                components={{
                  1: (
                    <a
                      className="font-bold"
                      href={getEnv('VITE_PLUGINS')}
                      target="_blank"
                      rel="noreferrer"
                    >
                      see documentation
                    </a>
                  ),
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default Plugins;
